import type { UrlEntity } from '../domain/note'
import type { GistUiStatus } from './gistController'

/**
 * The one line of text a link can show about its page: the generated gist when there is one, otherwise the
 * page's own description (free, already fetched for the preview). Every surface (card, row, chip action bar)
 * reads it from here, so they never disagree. `state` is only what the person should be told about a read.
 */
export interface LinkSummary {
  text: string | null
  source: 'gist' | 'site' | null
  state: Exclude<GistUiStatus, 'no-gist'> | null
  /** The technical reason behind a failure, appended small to its message. */
  detail?: string | null
}

const MIN_SITE_CHARS = 24
const MAX_SITE_CHARS = 220

/** What the person is told when a read they asked for did not produce a gist. */
export const GIST_STATE_MESSAGES: Record<Exclude<GistUiStatus, 'reading' | 'no-gist'>, string> = {
  unreadable: 'Sorry, this site blocks automated reading, so it cannot be summarized.',
  failed: 'Could not get a summary. Try again in a moment.',
  limit: 'Daily summary limit reached. It resets tomorrow.',
  'signed-out': 'Sign in to get summaries.',
}

/** The same, for a question the person asked about the page. */
/** The failure message for a question, with its technical reason when there is one. */
export const askFailureMessage = (state: Exclude<GistUiStatus, 'reading' | 'no-gist'>, detail: string | null): string =>
  state === 'failed' && detail ? `${ASK_STATE_MESSAGES.failed} (${detail})` : ASK_STATE_MESSAGES[state]

export const ASK_STATE_MESSAGES: Record<Exclude<GistUiStatus, 'reading' | 'no-gist'>, string> = {
  unreadable: GIST_STATE_MESSAGES.unreadable,
  failed: 'Could not get an answer. Try again in a moment.',
  limit: 'Daily limit reached. It resets tomorrow.',
  'signed-out': 'Sign in to ask about a page.',
}

/** What the person is told when a page cannot be read at all, named by the Worker's reason (checked before they ask). */
export function blockedMessage(reason: string): string {
  switch (reason) {
    case 'thin':
    case 'selector-empty': return 'This page has too little text to summarize.'
    case 'skipped-host': return 'Summaries and questions are not available for this kind of site.'
    case 'unusable-target': return 'This address cannot be read.'
    default: return 'Sorry, this site blocks automated reading, so it cannot be summarized.'
  }
}

/** The page's own description, as the site wrote it (not generated); null when there is none worth showing. */
export function siteDescriptionOf(url: UrlEntity): string | null {
  const text = (url.description ?? '').replace(/\s+/g, ' ').trim()
  if (text.length < MIN_SITE_CHARS || text === (url.title ?? '').trim()) return null
  if (text.length <= MAX_SITE_CHARS) return text
  const cut = text.slice(0, MAX_SITE_CHARS)
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), MAX_SITE_CHARS - 30)).replace(/[\s,;:.-]+$/, '')}…`
}

export function linkSummary(url: UrlEntity, status: GistUiStatus | null, detail: string | null = null): LinkSummary {
  const state = status === 'no-gist' ? null : status
  if (url.gist && !url.gist.hidden) return { text: url.gist.text, source: 'gist', state, detail }
  const site = siteDescriptionOf(url)
  return { text: site, source: site ? 'site' : null, state, detail }
}

/** The words a row or bar shows for a summary that has no text yet or could not be read. */
export function linkSummaryNote(summary: LinkSummary): string | null {
  if (summary.state === 'reading') return 'Reading the page…'
  if (summary.state) return summary.state === 'failed' && summary.detail ? `${GIST_STATE_MESSAGES.failed} (${summary.detail})` : GIST_STATE_MESSAGES[summary.state]
  return null
}
